import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import BrandName from "@/components/site/BrandName";
import { artwork } from "@/lib/brand-artwork";
import Button from "@/components/site/Button";

export default function BottomCTA() {
  return (
    <section
      id="ready"
      data-testid="bottom-cta-section"
      className="relative py-24 md:py-36 border-t border-white/[0.04] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#040914] via-[#070E1E] to-[#040914]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[640px] w-[640px] rounded-full bg-[#D4A65A]/[0.06] blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full bg-emerald-500/[0.05] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-4xl px-5 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/[0.05] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-emerald-300 font-semibold"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          The bar is inspection ready
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.75, delay: 0.05 }}
          className="mt-8 font-display text-[46px] sm:text-6xl md:text-[92px] tracking-[-0.045em] text-white leading-[0.95] font-semibold"
        >
          Ready for your
          <br />
          <span className="text-[#D4A65A]">next inspection?</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-8 text-slate-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
        >
          Your entire compliance system, training records and audit trail
          in one place. Signed, retained and ready the moment an inspector
          walks in.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            as="a"
            href="#contact"
            data-testid="bottom-book-demo"
            variant="primary"
            size="lg"
            className="group"
          >
            Book an inspection walkthrough
            <ArrowRight
              size={15}
              className="transition-transform group-hover:translate-x-1"
            />
          </Button>
          <a
            href="#tryit"
            data-testid="bottom-try-assessment"
            className="inline-flex items-center gap-2 text-[13px] text-slate-300 hover:text-white transition-colors"
          >
            Or try the sample assessment first
            <ArrowRight size={13} />
          </a>
        </motion.div>

        {/* Brand poster — subtle, below the CTA so it never competes with the H2 */}
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.75, delay: 0.15 }}
          className="relative mt-16 md:mt-20 mx-auto max-w-3xl"
          data-testid="bottom-cta-poster"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#050B18] shadow-[0_50px_140px_-50px_rgba(0,0,0,0.95)]">
            <div className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-emerald-500/[0.12] blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-[#D4A65A]/[0.1] blur-3xl" />

            <div className="relative flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-black/40 backdrop-blur">
              <div className="inline-flex items-center gap-2 text-[9.5px] uppercase tracking-[0.24em] text-[#D4A65A] font-semibold">
                <span className="h-1 w-3 bg-[#D4A65A]/80" />
                Brand · Poster
              </div>
              <span className="text-[9.5px] font-mono text-slate-500">
                cannicomply / social
              </span>
            </div>

            <img
              src={artwork.inspectionReady}
              alt="CanniComply poster: Inspection ready. Every day. Every policy acknowledged. Every assessment completed. Every certificate verifiable. Every audit event retained."
              loading="lazy"
              decoding="async"
              className="relative w-full h-auto block"
            />
          </div>
          <figcaption className="mt-3 text-center text-[11px] text-slate-500 tracking-wide">
            Inspection ready. Every day. — the CanniComply promise.
          </figcaption>
        </motion.figure>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold"
        >
          <span className="inline-flex items-center gap-2">
            <ShieldCheck size={11} className="text-emerald-400" /> SHA-256 chained
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-[#D4A65A]" /> UK region hosted
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-emerald-400" /> Retained 7 years
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-white/40" /> Built by <BrandName />
          </span>
        </motion.div>
      </div>
    </section>
  );
}
